import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlowCard } from '../components/GlowCard.js';

interface PipelineRun {
  id: string;
  pipelineId: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  durationMs: number | null;
}

interface PipelineHealth {
  pipelineId: string;
  name: string;
  status: string;
  successRate: number | null;
  errorCount: number;
  lastRunAt: string | null;
}

interface AnalyticsSummary {
  totalDistributed: number;
  distributedThisMonth: number;
  platformStats: Record<string, number>;
  recentRuns: PipelineRun[];
  pipelineHealth: PipelineHealth[];
  retrievedAt: string;
}

const PLATFORM_LABELS: Record<string, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
};

const PLATFORM_COLORS: Record<string, string> = {
  youtube: 'bg-red-500/20 text-red-400',
  instagram: 'bg-pink-500/20 text-pink-400',
  facebook: 'bg-blue-500/20 text-blue-400',
  linkedin: 'bg-sky-500/20 text-sky-400',
  tiktok: 'bg-white/10 text-white',
};

const STATUS_STYLES: Record<string, string> = {
  done: 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20',
  failed: 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
  running: 'bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20',
  pending: 'bg-white/5 text-white/60',
  active: 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20',
  paused: 'bg-yellow-500/10 text-yellow-400 ring-1 ring-yellow-500/20',
  error: 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
  draft: 'bg-white/5 text-white/60',
};

function formatDuration(ms: number | null): string {
  if (ms === null) return '--';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '--';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function StatCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <GlowCard className="rounded-xl border border-white/10 bg-pix-deep px-5 py-4">
      <p className="text-sm text-white/50">{label}</p>
      <p className="mt-1 text-2xl font-bold text-white">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-white/40">{sub}</p>}
    </GlowCard>
  );
}

function PlatformBar({ platform, count, max }: { platform: string; count: number; max: number }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  const colorClass = PLATFORM_COLORS[platform] ?? 'bg-pix-pink/10 text-pix-blue';
  const barColor = {
    youtube: 'bg-red-400',
    instagram: 'bg-pink-400',
    facebook: 'bg-blue-400',
    linkedin: 'bg-sky-400',
    tiktok: 'bg-gray-700',
  }[platform] ?? 'bg-indigo-400';

  return (
    <div className="flex items-center gap-3">
      <span
        className={[
          'inline-flex w-16 shrink-0 items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold',
          colorClass,
        ].join(' ')}
      >
        {PLATFORM_LABELS[platform] ?? platform}
      </span>
      <div className="relative flex-1 overflow-hidden rounded-full bg-white/5" style={{ height: 8 }}>
        <div
          className={['absolute inset-y-0 left-0 rounded-full transition-[width] duration-500', barColor].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-semibold text-white/70">{count}</span>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      const result = await api.get<AnalyticsSummary>('/api/analytics/summary');
      if (result.data) {
        setSummary(result.data);
      } else {
        setError('Failed to load analytics.');
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-pix-pink border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400 ring-1 ring-red-500/20">
        {error}
      </div>
    );
  }

  const hasData = summary && (summary.totalDistributed > 0 || summary.pipelineHealth.length > 0);

  if (!hasData) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="mt-1 text-sm text-white/50">Overview of your content pipeline activity.</p>
        <div className="mt-6">
          <EmptyState
            icon={
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
              </svg>
            }
            headline="No activity yet"
            subtext="Create a pipeline, connect TikTok, and run it to see analytics here."
            cta={{ label: 'Create a pipeline', onClick: () => navigate('/dashboard/pipelines') }}
          />
        </div>
      </div>
    );
  }

  const platformEntries = Object.entries(summary.platformStats).sort(([, a], [, b]) => b - a);
  const maxPlatformCount = platformEntries.length > 0 ? Math.max(...platformEntries.map(([, v]) => v)) : 1;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-white/50">Overview of your content pipeline activity.</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total distributed"
          value={summary.totalDistributed}
          sub="all time"
        />
        <StatCard
          label="Distributed this month"
          value={summary.distributedThisMonth}
          sub={new Date().toLocaleString('default', { month: 'long' })}
        />
        <StatCard
          label="Pipelines"
          value={summary.pipelineHealth.length}
          sub={`${summary.pipelineHealth.filter((p) => p.status === 'active').length} active`}
        />
        <StatCard
          label="Runs (last 10)"
          value={summary.recentRuns.filter((r) => r.status === 'done').length}
          sub={`of ${summary.recentRuns.length} succeeded`}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Platform breakdown */}
        {platformEntries.length > 0 && (
          <GlowCard className="rounded-xl border border-white/10 bg-pix-deep px-5 py-4">
            <h2 className="text-sm font-semibold text-white">Platform breakdown</h2>
            <p className="mt-0.5 text-xs text-white/40">Published items per platform</p>
            <div className="mt-4 space-y-3">
              {platformEntries.map(([platform, count]) => (
                <PlatformBar key={platform} platform={platform} count={count} max={maxPlatformCount} />
              ))}
            </div>
          </GlowCard>
        )}

        {/* Pipeline health */}
        {summary.pipelineHealth.length > 0 && (
          <GlowCard className="rounded-xl border border-white/10 bg-pix-deep px-5 py-4">
            <h2 className="text-sm font-semibold text-white">Pipeline health</h2>
            <p className="mt-0.5 text-xs text-white/40">Success rate across recent runs</p>
            <div className="mt-4 space-y-3">
              {summary.pipelineHealth.map((pipeline) => (
                <div key={pipeline.pipelineId} className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{pipeline.name}</p>
                    <p className="text-xs text-white/40">
                      Last run {formatRelativeTime(pipeline.lastRunAt)}
                      {pipeline.errorCount > 0 && ` · ${pipeline.errorCount} error${pipeline.errorCount !== 1 ? 's' : ''}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {pipeline.successRate !== null && (
                      <span
                        className={[
                          'text-sm font-semibold',
                          pipeline.successRate >= 80
                            ? 'text-green-400'
                            : pipeline.successRate >= 50
                            ? 'text-yellow-400'
                            : 'text-red-400',
                        ].join(' ')}
                      >
                        {pipeline.successRate}%
                      </span>
                    )}
                    <span
                      className={[
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_STYLES[pipeline.status] ?? 'bg-white/5 text-white/60',
                      ].join(' ')}
                    >
                      {pipeline.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </GlowCard>
        )}
      </div>

      {/* Recent pipeline runs */}
      {summary.recentRuns.length > 0 && (
        <GlowCard className="mt-6 rounded-xl border border-white/10 bg-pix-deep">
          <div className="border-b border-white/5 px-5 py-4">
            <h2 className="text-sm font-semibold text-white">Recent pipeline runs</h2>
            <p className="mt-0.5 text-xs text-white/40">Last {summary.recentRuns.length} runs across all pipelines</p>
          </div>
          <div className="divide-y divide-white/5">
            {summary.recentRuns.map((run) => {
              const pipeline = summary.pipelineHealth.find((p) => p.pipelineId === run.pipelineId);
              return (
                <div key={run.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white/70">
                      {pipeline?.name ?? <span className="text-white/40">Pipeline</span>}
                    </p>
                    {run.errorMessage && (
                      <p className="mt-0.5 truncate text-xs text-red-500">{run.errorMessage}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-right">
                    <span className="text-xs text-white/40">{formatRelativeTime(run.startedAt)}</span>
                    <span className="w-10 text-right text-xs text-white/40">{formatDuration(run.durationMs)}</span>
                    <span
                      className={[
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_STYLES[run.status] ?? 'bg-white/5 text-white/60',
                      ].join(' ')}
                    >
                      {run.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </GlowCard>
      )}
    </div>
  );
}
